/**
 * Small helper components used by the AdminProductEdit page.
 * Extracted from AdminProductEdit.jsx to keep the main page under 1000 lines.
 * These are pure presentational + one small file-upload widget — no product state
 * lives here.
 */
import React, { useState } from "react";
import {
  Sparkles, Plus, X, Loader2, CheckCircle2, XCircle, RotateCw, Undo2,
} from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { uploadFileSmart } from "@/lib/chunkedUpload";
import { errorDetail } from "@/lib/utils";

export function emptySuggestions() {
  return {
    recommended_applications: [],
    maintenance_instructions: [],
    faqs: [],
    use_cases: [],
    installation_recommendations: [],
  };
}

export function Section({ title, action, children }) {
  return (
    <div className="heda-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-heda-line px-5 py-3">
        <h3 className="min-w-0 flex-1 font-display text-[15px] font-bold text-heda-ink">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="heda-label">{label}</label>
      {children}
    </div>
  );
}

export function ListEditor({ items, onChange, onAdd, onRemove, testId, placeholder }) {
  return (
    <div className="space-y-2">
      {(items || []).map((v, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            data-testid={`${testId}-input-${idx}`}
            value={v}
            placeholder={placeholder}
            onChange={(e) => onChange(idx, e.target.value)}
            className="heda-input min-w-0 flex-1 py-2"
          />
          <button
            onClick={() => onRemove(idx)}
            className="hed-icon-btn hover:bg-red-50 hover:text-red-700"
          >
            <X size={15} />
          </button>
        </div>
      ))}
      <button
        onClick={onAdd}
        className="heda-pill mt-2"
        data-testid={`${testId}-add-button`}
      >
        <Plus size={12} /> Add item
      </button>
    </div>
  );
}

export function ReextractButton({ field, label, busy, onClick, canUndo, onUndo }) {
  return (
    <div className="flex items-center gap-1.5">
      {canUndo && (
        <button
          type="button"
          onClick={onUndo}
          disabled={!!busy}
          title={`Undo last AI re-extraction of "${label}"`}
          data-testid={`undo-${field}`}
          className="hed-mini-btn uppercase tracking-wider hover:border-red-300 hover:bg-red-50 hover:text-red-700"
        >
          <Undo2 size={10} />
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={!!busy}
        title={`Re-extract "${label}" from the original PDF using AI`}
        data-testid={`reextract-${field}`}
        className="hed-mini-btn uppercase tracking-wider hover:bg-heda-gold-tint"
      >
        {busy ? (
          <Loader2 size={10} className="animate-spin text-heda-gold" />
        ) : (
          <RotateCw size={10} />
        )}
        AI Re-extract
      </button>
    </div>
  );
}

export function SuggestionGroup({ title, items, isApproved, toggle, testIdPrefix }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className="hed-overline mb-2">{title}</div>
      <div className="space-y-1.5">
        {items.map((v, idx) => {
          const approved = isApproved(v);
          return (
            <div
              key={idx}
              className={`flex items-start gap-2 rounded-xl border p-2.5 text-sm transition ${
                approved ? "border-heda-olive/40 bg-heda-olive-tint/40" : "border-heda-line bg-white"
              }`}
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <button
                onClick={() => toggle(v)}
                className={`mt-0.5 flex-shrink-0 rounded-full transition ${
                  approved ? "text-emerald-700" : "text-heda-mute hover:text-emerald-700"
                }`}
                data-testid={`${testIdPrefix}-toggle-${idx}`}
              >
                {approved ? (
                  <CheckCircle2 size={16} />
                ) : (
                  <XCircle size={16} />
                )}
              </button>
              <div className="flex-1">{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SuggestionFaqGroup({ items, isApproved, toggle }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className="hed-overline mb-2">FAQs</div>
      <div className="space-y-1.5">
        {items.map((f, idx) => {
          const approved = isApproved(f);
          return (
            <div
              key={idx}
              className={`flex items-start gap-2 rounded-xl border p-2.5 text-sm transition ${
                approved ? "border-heda-olive/40 bg-heda-olive-tint/40" : "border-heda-line bg-white"
              }`}
              data-testid={`sugg-faq-item-${idx}`}
            >
              <button
                onClick={() => toggle(f)}
                className={`mt-0.5 flex-shrink-0 rounded-full transition ${
                  approved ? "text-emerald-700" : "text-heda-mute hover:text-emerald-700"
                }`}
                data-testid={`sugg-faq-toggle-${idx}`}
              >
                {approved ? (
                  <CheckCircle2 size={16} />
                ) : (
                  <XCircle size={16} />
                )}
              </button>
              <div className="flex-1">
                <div className="font-medium">{f.question}</div>
                <div className="mt-1 text-xs text-heda-sub">{f.answer}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Small widget for attaching additional PDFs/images to an existing product
 * without breaking the product's QR code (attach → optional re-extract).
 */
export function SourceFilesEditor({ productId, files, onChange }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    try {
      const uploadedIds = { documents: [], images: [] };
      for (const f of fileList) {
        const isImage = (f.type || "").startsWith("image/");
        // Smart helper routes files >6 MB through chunked upload so 100 MB
        // brochures don't get killed by Cloudflare / K8s body-size limits.
        const data = await uploadFileSmart(f, { kind: isImage ? "image" : "document" });
        if (isImage) uploadedIds.images.push(data.id);
        else uploadedIds.documents.push(data.id);
      }
      const { data } = await api.post(`/products/${productId}/attach-files`, {
        file_ids: uploadedIds.documents,
        image_ids: uploadedIds.images,
      });
      onChange({ files: data.files || [], images: data.images || [] });
      if (
        window.confirm(
          "Files attached. Run AI extraction now so the new content is merged into this product?\n\n(Other fields are kept — AI re-extracts everything across the combined file set.)",
        )
      ) {
        try {
          await api.post(`/products/${productId}/extract`);
        } catch (e) {
          alert(errorDetail(e) || "Could not start extraction");
        }
      }
    } catch (e) {
      alert(`Upload failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async (fileId) => {
    if (!window.confirm("Remove this PDF from the product?\n\nThe file stays in your storage but is no longer used for AI extraction.")) return;
    try {
      await api.delete(`/products/${productId}/files/${fileId}`);
      const { data } = await api.get(`/products/${productId}`);
      onChange({ files: data.files || [], images: data.images || [] });
    } catch (e) {
      alert(errorDetail(e) || "Could not detach file");
    }
  };

  return (
    <div className="space-y-3">
      {(files || []).length > 0 ? (
        <div className="space-y-2">
          {files.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-heda-line bg-heda-cream/50 px-3 py-2 text-sm"
              data-testid={`source-file-${f.id}`}
            >
              <a
                href={fileUrl(f.storage_path)}
                target="_blank"
                rel="noreferrer"
                className="truncate flex items-center gap-2 flex-1 min-w-0 text-heda-ink hover:text-heda-gold transition-colors"
              >
                <Sparkles size={12} className="flex-shrink-0 text-heda-gold" />
                <span className="truncate">{f.original_filename}</span>
                <span className="flex-shrink-0 text-[11.5px] text-heda-mute">
                  {Math.round((f.size || 0) / 1024)} KB
                </span>
              </a>
              <button
                type="button"
                onClick={() => handleDetach(f.id)}
                disabled={busy}
                className="flex-shrink-0 rounded-full p-1.5 text-heda-mute hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                data-testid={`detach-file-${f.id}`}
                title="Remove this PDF from the product"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[13px] text-heda-mute">
          No PDFs attached yet. Drop one below — the AI will read it.
        </div>
      )}

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleUpload(Array.from(e.dataTransfer.files));
        }}
        className={`block cursor-pointer rounded-xl border-2 border-dashed px-3 py-4 text-center transition ${
          drag ? "border-heda-gold bg-heda-gold-tint" : "border-heda-line bg-heda-cream/40 hover:border-heda-gold"
        } ${busy ? "cursor-wait opacity-50" : ""}`}
        data-testid="attach-pdf-dropzone"
      >
        <input
          type="file"
          accept=".pdf,application/pdf,image/*"
          multiple
          disabled={busy}
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          data-testid="attach-pdf-input"
        />
        {busy ? (
          <div className="inline-flex items-center gap-2 text-xs text-heda-mute">
            <Loader2 size={14} className="animate-spin text-heda-gold" /> Attaching…
          </div>
        ) : (
          <div className="text-xs">
            <span className="font-medium text-heda-ink">+ Add more PDFs</span>{" "}
            <span className="text-heda-mute">— drop or click to attach. QR stays the same.</span>
          </div>
        )}
      </label>
    </div>
  );
}
